import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useApiSqlContext } from 'src/modules/contexts/ApiSqlContext';
import {
    ApiError,
    createNewClientResponse,
    getNewClientsDraft,
    newClientDraftResponse,
    postAdminCreateNewClientSaveUpdateDraft,
    putClientDrafteResponse,
} from 'src/modules/generated-api-clients/sql';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { AxiosError } from 'axios';

export const useGetClientDraftsQuery = (options = {}) => {
    const { client } = useApiSqlContext();
    const { user } = useAuthenticator(context => [context.user]);

    const token = user.getSignInUserSession()?.getIdToken().getJwtToken();

    if (!client) {
        throw new Error('API client is not initialized');
    }

    return useQuery<getNewClientsDraft, ApiError>(
        'getAdminPortalClientDraft',
        async () => {
            try {
                if (token === undefined) {
                    throw new Error(
                        'AWS Amplify user attributes cannot be undefined'
                    );
                }

                return await client.default.getAdminPortalClientDraft(token);
            } catch (error: unknown) {
                if (error instanceof ApiError) {
                    // Handle ApiError specifically
                    console.error(
                        `ApiError -> URL: ${error.url}\nSTATUS: ${error.status}\nMESSAGE: ${
                            error.message
                        }\nBODY: ${JSON.stringify(error.body)}`
                    );
                    throw error;
                } else if (error instanceof AxiosError) {
                    console.error(`AxiosError -> ${JSON.stringify(error)}`);
                } else {
                    // Handle other errors
                    console.error('Error:', error);
                    throw error;
                }
                return {} as getNewClientsDraft;
            }
        },
        {
            enabled: !!token,
            ...options,
        }
    );
};

export const usePostClientDraftMutation = () => {
    const queryClient = useQueryClient();
    const { client } = useApiSqlContext();
    const { user } = useAuthenticator(context => [context.user]);

    const token = user.getSignInUserSession()?.getIdToken().getJwtToken();

    if (!client) {
        throw new Error('API client is not initialized');
    }

    return useMutation<
        newClientDraftResponse,
        ApiError,
        postAdminCreateNewClientSaveUpdateDraft
    >(
        (draft: postAdminCreateNewClientSaveUpdateDraft) => {
            // Guard against errors
            if (!draft) {
                throw new Error('Invalid Client draft.');
            }

            if (token === undefined) {
                throw new Error(
                    'AWS Amplify user attributes cannot be undefined'
                );
            }

            return client.default.postAdminPortalClientDraft(token, draft);
        },
        {
            onSuccess: (_newClientDraftResponse: newClientDraftResponse) => {
                queryClient.invalidateQueries('getAdminPortalClientDraft');
            },
            onError: (error: unknown) => {
                if (error instanceof ApiError) {
                    // Handle ApiError specifically
                    console.error(
                        `ApiError -> URL: ${error.url}\nSTATUS: ${error.status}\nMESSAGE: ${
                            error.message
                        }\nBODY: ${JSON.stringify(error.body)}`
                    );
                } else {
                    // Handle other errors
                    console.error('Error:', error);
                }
            },
        }
    );
};

export const usePutClientDraftMutation = () => {
    const queryClient = useQueryClient();
    const { client } = useApiSqlContext();
    const { user } = useAuthenticator(context => [context.user]);
    const token = user.getSignInUserSession()?.getIdToken().getJwtToken();

    if (!client) {
        throw new Error('API client is not initialized');
    }
    type putAdminPortalClientDraftParams = {
        id: string;
        draft: postAdminCreateNewClientSaveUpdateDraft;
    };

    return useMutation<
        putClientDrafteResponse,
        ApiError,
        putAdminPortalClientDraftParams
    >(
        (params: putAdminPortalClientDraftParams) => {
            // Guard against errors
            if (!params) {
                throw new Error('Invalid Client draft.');
            }
            if (token === undefined) {
                throw new Error(
                    'AWS Amplify user attributes cannot be undefined'
                );
            }

            return client.default.putAdminPortalClientDraft(
                token,
                params.id,
                params.draft
            );
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries('getAdminPortalClientDraft');
            },
            onError: (error: unknown) => {
                if (error instanceof ApiError) {
                    // Handle ApiError specifically
                    console.error(
                        `ApiError -> URL: ${error.url}\nSTATUS: ${error.status}\nMESSAGE: ${
                            error.message
                        }\nBODY: ${JSON.stringify(error.body)}`
                    );
                } else {
                    // Handle other errors
                    console.error('Error:', error);
                }
            },
        }
    );
};

export const usePostClientMutation = () => {
    const queryClient = useQueryClient();
    const { client } = useApiSqlContext();
    const { user } = useAuthenticator(context => [context.user]);

    const token = user.getSignInUserSession()?.getIdToken().getJwtToken();

    if (!client) {
        throw new Error('API client is not initialized');
    }

    return useMutation<
        createNewClientResponse,
        ApiError,
        postAdminCreateNewClientSaveUpdateDraft
    >(
        (draft: postAdminCreateNewClientSaveUpdateDraft) => {
            if (token === undefined) {
                throw new Error(
                    'AWS Amplify user attributes cannot be undefined'
                );
            }
            return client.default.postAdminPortalClientCreateNewClient(
                token,
                draft
            );
        },
        {
            onSuccess: (_newClientResponse: createNewClientResponse) => {
                queryClient.invalidateQueries(
                    'getAdminPortalHomeLandingSummary'
                );
                queryClient.invalidateQueries('getAdminPortalClientDraft');
            },
            onError: (error: unknown) => {
                if (error instanceof ApiError) {
                    // Handle ApiError specifically
                    console.error(
                        `ApiError -> URL: ${error.url}\nSTATUS: ${error.status}\nMESSAGE: ${
                            error.message
                        }\nBODY: ${JSON.stringify(error.body)}`
                    );
                } else {
                    // Handle other errors
                    console.error('Error:', error);
                }
            },
        }
    );
};

export const useDeleteClientDraftMutation = () => {
    const queryClient = useQueryClient();
    const { client } = useApiSqlContext();
    const { user } = useAuthenticator(context => [context.user]);
    const token = user.getSignInUserSession()?.getIdToken().getJwtToken();

    if (!client) {
        throw new Error('API client is not initialized');
    }

    return useMutation<putClientDrafteResponse, ApiError, string>(
        (id: string) => {
            if (token === undefined) {
                throw new Error(
                    'AWS Amplify user attributes cannot be undefined'
                );
            }
            return client.default.deleteAdminPortalClientDraft(token, id);
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries('getAdminPortalClientDraft');
            },
            onError: (error: unknown) => {
                if (error instanceof ApiError) {
                    // Handle ApiError specifically
                    console.error(
                        `ApiError -> URL: ${error.url}\nSTATUS: ${error.status}\nMESSAGE: ${
                            error.message
                        }\nBODY: ${JSON.stringify(error.body)}`
                    );
                } else {
                    // Handle other errors
                    console.error('Error:', error);
                }
            },
        }
    );
};
